import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceV3 } from '../../../../backendsdk';
import { TrackedButton as Button } from '../../../../components/TrackedComponents';
import useDrivers, { getDriverDisplayName } from '../../../../hooks/drivers';
import useProfile from '../../../../hooks/profile';

interface GeneralDetailsPanelProps {
    device: DeviceV3;
    onLiveStreamClick?: () => void;
    isAdmin?: boolean;
}

const GeneralDetailsPanel: React.FC<GeneralDetailsPanelProps> = ({
    device,
    onLiveStreamClick,
    isAdmin,
}: GeneralDetailsPanelProps) => {
    const { t } = useTranslation();
    const { profile } = useProfile();
    const { getDriver } = useDrivers();
    const showDriverCode = profile.customer.settings.show_driver_code ?? false;

    const defaultDriver = getDriver(device.device.default_driver);
    const trimmedDriverName = defaultDriver?.is_auto_generated
        ? undefined
        : (defaultDriver
              ? getDriverDisplayName(defaultDriver, showDriverCode).trim()
              : device.device.driver_name.trim()) || undefined;
    const driver = getDriver(device.current_trip?.driver);
    const subFleet = driver?.sub_fleet ? driver.sub_fleet : device.device.customer_sub_fleet;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: !!device.device.customer_sub_fleet ? 92 : undefined,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                    <Typography sx={{ fontSize: 12, color: 'grayColor.main', fontWeight: 'bold' }}>
                        {t('content.fleet.details.general_details.default_driver_name')}
                    </Typography>
                    <Typography
                        id="driver-name-field"
                        data-testid="driver-name-field"
                        sx={{ fontSize: 16, color: !!trimmedDriverName ? undefined : 'grayColor.main' }}
                    >
                        {!!trimmedDriverName ? trimmedDriverName : t('content.fleet.unknown_driver')}
                    </Typography>
                </Box>
                {!!subFleet ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: 12, color: 'grayColor.main', fontWeight: 'bold' }}>
                            {t('content.fleet.details.general_details.sub_fleet')}
                        </Typography>
                        <Typography id="sub-fleet-field" data-testid="sub-fleet-field" sx={{ fontSize: 16 }}>
                            {subFleet}
                        </Typography>
                    </Box>
                ) : null}
            </Box>
            {isAdmin && onLiveStreamClick && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        id="live-stream-btn"
                        variant="contained"
                        color="primary"
                        onClick={onLiveStreamClick}
                        sx={{ ml: 2 }}
                    >
                        {t('content.fleet.details.general_details.live_stream.label')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default GeneralDetailsPanel;
